import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown } from 'lucide-react';

/**
 * FAQSection — reusable accordion FAQ block with FAQPage JSON-LD schema.
 *
 * Renders a visible <details>/<summary> Q&A list so AI crawlers (ChatGPT,
 * Perplexity, Claude, Google AI Overviews) and traditional search engines
 * extract the answers directly from static HTML — <details> keeps content
 * in the DOM even when collapsed.
 *
 * Also injects schema.org FAQPage JSON-LD via react-helmet-async for rich
 * results eligibility.
 *
 * Props:
 *  - title?: string                       Section heading (default "Frequently asked questions")
 *  - faqs: Array<{question, answer}>      Required. Q&A items.
 *  - id?: string                          Optional anchor id for the section (default "faq")
 */
export default function FAQSection({ title = 'Frequently asked questions', faqs, id = 'faq' }) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;

  const headingId = `${id}-heading`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section
        id={id}
        aria-labelledby={headingId}
        className="py-16 px-6 bg-[#F4F0FA] border-t border-[#E8E0F0]"
      >
        <div className="max-w-3xl mx-auto">
          <h2
            id={headingId}
            className="text-2xl md:text-3xl font-bold text-[#341671] mb-8 text-center"
          >
            {title}
          </h2>

          <div className="space-y-3">
            {faqs.map((item, i) => (
              <details
                key={i}
                className="group bg-white border border-[#E8E0F0] rounded-xl overflow-hidden open:shadow-sm transition-shadow"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 text-left text-base font-semibold text-[#341671] hover:bg-[#F4F0FA] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#422680] focus-visible:ring-offset-2">
                  <span>{item.question}</span>
                  <ChevronDown
                    className="w-5 h-5 text-[#660F56] flex-shrink-0 transition-transform duration-200 group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <div className="px-5 pb-5 pt-1 text-sm leading-relaxed text-[#1A1033] border-t border-[#E8E0F0]">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
